import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LozengeVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'pink'
  | 'primary-outline'
  | 'success-outline'
  | 'error-outline'
  | 'info-outline'
  | 'pink-outline';

export type LozengeShape = 'flat' | 'pill';
export type LozengeSize = 'sm' | 'md' | 'lg';

export interface LozengeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: LozengeVariant;
  shape?: LozengeShape;
  size?: LozengeSize;
  title?: string | null;
  hasDot?: boolean;
  icon?: string;
  disabled?: boolean;
  onClose?: () => void;
  semanticId?: string;
  children?: React.ReactNode;
  /** Play the pop-in scale animation on mount. @default false */
  animated?: boolean;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const popIn = keyframes`
  0%   { transform: scale(0.8); opacity: 0; }
  60%  { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Variant token maps ────────────────────────────────────────────────────────

interface VariantTokens {
  bg: string;
  color: string;
  border: string;
  dot: string;
  closeFg: string;
  closeHoverBg: string;
}

const variantMap: Record<LozengeVariant, VariantTokens> = {
  default: {
    bg: 'var(--ui-background-muted)',
    color: 'var(--ui-text-muted)',
    border: 'transparent',
    dot: 'var(--ui-text-subtle)',
    closeFg: 'var(--ui-text-subtle)',
    closeHoverBg: 'var(--ui-border)',
  },
  primary: {
    bg: 'var(--ui-interactive-light)',
    color: 'var(--ui-interactive)',
    border: 'transparent',
    dot: 'var(--ui-interactive)',
    closeFg: 'var(--ui-interactive)',
    closeHoverBg: 'rgba(var(--ui-interactive-rgb, 100 62 173) / 0.15)',
  },
  success: {
    bg: 'var(--ui-status-success-muted)',
    color: 'var(--ui-status-success)',
    border: 'transparent',
    dot: 'var(--ui-status-success)',
    closeFg: 'var(--ui-status-success)',
    closeHoverBg: 'rgba(var(--ui-status-success-rgb, 47 186 121) / 0.15)',
  },
  error: {
    bg: 'var(--ui-status-danger-muted)',
    color: 'var(--ui-status-danger)',
    border: 'transparent',
    dot: 'var(--ui-status-danger)',
    closeFg: 'var(--ui-status-danger)',
    closeHoverBg: 'rgba(var(--ui-status-danger-rgb, 219 47 47) / 0.12)',
  },
  info: {
    bg: 'var(--ui-status-info-muted, #F0F2FF)',
    color: 'var(--ui-status-info, #5C6AC4)',
    border: 'transparent',
    dot: 'var(--ui-status-info, #5C6AC4)',
    closeFg: 'var(--ui-status-info, #5C6AC4)',
    closeHoverBg: 'rgba(92 106 196 / 0.12)',
  },
  warning: {
    bg: 'var(--ui-status-warning-muted)',
    color: 'var(--ui-status-warning)',
    border: 'transparent',
    dot: 'var(--ui-status-warning)',
    closeFg: 'var(--ui-status-warning)',
    closeHoverBg: 'rgba(245 166 35 / 0.15)',
  },
  pink: {
    bg: 'var(--ui-status-pink-muted)',
    color: 'var(--ui-status-pink)',
    border: 'transparent',
    dot: 'var(--ui-status-pink)',
    closeFg: 'var(--ui-status-pink)',
    closeHoverBg: 'rgba(241 120 182 / 0.15)',
  },
  'primary-outline': {
    bg: 'transparent',
    color: 'var(--ui-interactive)',
    border: 'var(--ui-interactive)',
    dot: 'var(--ui-interactive)',
    closeFg: 'var(--ui-interactive)',
    closeHoverBg: 'rgba(var(--ui-interactive-rgb, 100 62 173) / 0.1)',
  },
  'success-outline': {
    bg: 'transparent',
    color: 'var(--ui-status-success)',
    border: 'var(--ui-status-success)',
    dot: 'var(--ui-status-success)',
    closeFg: 'var(--ui-status-success)',
    closeHoverBg: 'rgba(47 186 121 / 0.1)',
  },
  'error-outline': {
    bg: 'transparent',
    color: 'var(--ui-status-danger)',
    border: 'var(--ui-status-danger)',
    dot: 'var(--ui-status-danger)',
    closeFg: 'var(--ui-status-danger)',
    closeHoverBg: 'rgba(219 47 47 / 0.08)',
  },
  'info-outline': {
    bg: 'transparent',
    color: 'var(--ui-status-info, #5C6AC4)',
    border: 'var(--ui-status-info, #5C6AC4)',
    dot: 'var(--ui-status-info, #5C6AC4)',
    closeFg: 'var(--ui-status-info, #5C6AC4)',
    closeHoverBg: 'rgba(92 106 196 / 0.08)',
  },
  'pink-outline': {
    bg: 'transparent',
    color: 'var(--ui-status-pink)',
    border: 'var(--ui-status-pink)',
    dot: 'var(--ui-status-pink)',
    closeFg: 'var(--ui-status-pink)',
    closeHoverBg: 'rgba(241 120 182 / 0.08)',
  },
};

// ─── Styled components ────────────────────────────────────────────────────────

const Root = styled.span.withConfig({
  shouldForwardProp: (prop) => !['$variant', '$shape', '$size', '$hasClose', '$animated'].includes(String(prop)),
})<{
  $variant: LozengeVariant;
  $shape: LozengeShape;
  $size: LozengeSize;
  $hasClose: boolean;
  $animated: boolean;
}>`
  --lz-bg: ${({ $variant }) => variantMap[$variant].bg};
  --lz-color: ${({ $variant }) => variantMap[$variant].color};
  --lz-border: ${({ $variant }) => variantMap[$variant].border};
  --lz-dot: ${({ $variant }) => variantMap[$variant].dot};
  --lz-close-fg: ${({ $variant }) => variantMap[$variant].closeFg};
  --lz-close-hover-bg: ${({ $variant }) => variantMap[$variant].closeHoverBg};

  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  vertical-align: middle;
  border-radius: ${({ $shape }) =>
    $shape === 'pill' ? `var(--ui-radius-full, ${rem(33)})` : `var(--ui-radius-sm, ${rem(4)})`};
  border: 1px solid var(--lz-border);
  background: var(--lz-bg);
  color: var(--lz-color);
  font-family: var(--ui-font-sans);
  font-weight: 500;
  cursor: default;
  user-select: none;
  ${({ $animated }) =>
    $animated &&
    css`
      animation: ${popIn} 180ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
    `}
  transition:
    background 150ms ease,
    border-color 150ms ease,
    box-shadow 150ms ease;

  /* size: md (default) */
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  padding: ${({ $shape, $hasClose }) =>
    $shape === 'pill' ? `${rem(5)} ${rem(12)}` : `${rem(3)} ${rem(6)}`};
  gap: ${rem(4)};

  ${({ $size }) =>
    $size === 'sm' &&
    css`
      font-size: ${rem(10)};
      line-height: ${rem(12)};
      padding: ${rem(2)} ${rem(6)};
      gap: ${rem(3)};
    `}

  ${({ $size, $shape }) =>
    $size === 'lg' &&
    css`
      font-size: ${rem(14)};
      line-height: ${rem(18)};
      padding: ${$shape === 'pill' ? `${rem(6)} ${rem(14)}` : `${rem(6)} ${rem(12)}`};
      gap: ${rem(6)};
    `}

  /* outline variants get a subtle inset shadow */
  ${({ $variant }) =>
    $variant.includes('outline') &&
    css`
      box-shadow: inset 0 0 0 0 transparent;
      &:hover {
        background: var(--lz-close-hover-bg);
      }
    `}
`;

const Dot = styled.span`
  flex-shrink: 0;
  width: ${rem(4)};
  height: ${rem(4)};
  border-radius: 50%;
  background: var(--lz-dot);
`;

const LeadIcon = styled.span`
  flex-shrink: 0;
  font-family: 'Material Symbols Outlined';
  font-size: ${rem(12)};
  font-weight: 400;
  line-height: 1;
  color: var(--lz-color);
  user-select: none;

  [data-size='lg'] > & {
    font-size: ${rem(16)};
  }
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
  letter-spacing: 0.01em;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(14)};
  height: ${rem(14)};
  border-radius: 50%;
  border: none;
  padding: 0;
  margin-left: ${rem(2)};
  background: transparent;
  color: var(--lz-close-fg);
  cursor: pointer;
  transition:
    background 120ms ease,
    transform 120ms ease,
    opacity 120ms ease;
  line-height: 1;

  .material-symbols-outlined {
    font-size: ${rem(11)};
    font-weight: 600;
    line-height: 1;
    display: block;
  }

  [data-size='lg'] > & {
    width: ${rem(18)};
    height: ${rem(18)};

    .material-symbols-outlined {
      font-size: ${rem(14)};
    }
  }

  &:hover {
    background: var(--lz-close-hover-bg);
    transform: scale(1.15);
  }

  &:active {
    transform: scale(0.9);
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--ui-focus-ring);
    outline-offset: 1px;
  }

  &.is-active {
    opacity: 0.45;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const Lozenge = React.forwardRef<HTMLSpanElement, LozengeProps>(function Lozenge(
  {
    variant = 'default',
    shape = 'flat',
    size = 'md',
    title,
    hasDot = false,
    icon,
    disabled = false,
    onClose,
    semanticId,
    animated = false,
    children,
    className,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const label = title ?? children;
  const hasClose = !disabled && onClose != null;

  return (
    <Root
      ref={ref}
      $variant={variant}
      $shape={shape}
      $size={size}
      $hasClose={hasClose}
      $animated={animated}
      data-variant={variant}
      data-shape={shape}
      data-size={size}
      className={className}
      {...semanticProps}
      {...rest}
    >
      {hasDot && <Dot aria-hidden />}
      {icon != null && !hasDot && (
        <LeadIcon className="material-symbols-outlined" aria-hidden>
          {icon}
        </LeadIcon>
      )}
      <Label>{label}</Label>
      {hasClose && (
        <CloseBtn
          type="button"
          aria-label={`Remove ${typeof label === 'string' ? label : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <span className="material-symbols-outlined" aria-hidden>
            close
          </span>
        </CloseBtn>
      )}
    </Root>
  );
});

Lozenge.displayName = 'Lozenge';
